import React, { useState, useEffect } from 'react';
import { Layout } from './components/Layout';
import { ReportEditor } from './components/ReportEditor';
import { ServiceReport, Customer, ReportTemplate } from './types';
import { saveLocalReport, getAllLocalReports } from './services/db';
import { generateEdgeCaseReportContent } from './services/geminiService';
import { fetchCustomerDirectory } from './services/workDriveService';
import { generateUUID } from './utils/helpers';
import { generateHTML, generateMarkdown } from './utils/exportUtils';
import { useAuth, RequireAuth, RequireAdmin, LoginPage } from './src/auth';
import { AuthDebugPanel } from './src/components/AuthDebugPanel';
import { AdminUsersPanel } from './src/components/AdminUsersPanel';

const ServiceReportApp: React.FC = () => {
    const { user, loading, signOut } = useAuth();
    const [view, setView] = useState<'list' | 'editor'>('list');
    const [currentReportId, setCurrentReportId] = useState<string | undefined>(undefined);
    const [initialTemplate, setInitialTemplate] = useState<ReportTemplate>('SERVICE');
    const [showNewReportModal, setShowNewReportModal] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [reports, setReports] = useState<ServiceReport[]>([]);
    const [loadingReports, setLoadingReports] = useState(false);

    // Initial Load of Reports
    useEffect(() => {
        if (user && view === 'list') {
            loadReports();
        }
    }, [user, view]);

    const loadReports = async () => {
        setLoadingReports(true);
        try {
            const data = await getAllLocalReports();
            const sorted = data.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
            setReports(sorted);
        } catch (e) {
            console.error("Failed to load reports", e);
        } finally {
            setLoadingReports(false);
        }
    };

    const handleNewReport = (type: ReportTemplate) => {
        setInitialTemplate(type);
        setCurrentReportId(undefined);
        setView('editor');
        setShowNewReportModal(false);
    };

    const downloadArtifact = (e: React.MouseEvent, report: ServiceReport, type: 'html' | 'json' | 'md') => {
        e.stopPropagation();
        let content = '';
        let mime = '';
        let ext = '';

        try {
            if (type === 'html') {
                content = generateHTML(report);
                mime = 'text/html';
                ext = 'html';
            } else if (type === 'md') {
                content = generateMarkdown(report);
                mime = 'text/markdown';
                ext = 'md';
            } else {
                const { _syncState, ...clean } = report;
                content = JSON.stringify(clean, null, 2);
                mime = 'application/json';
                ext = 'json';
            }

            const filename = `ServiceReport_${report.reportId || 'Draft'}_${report.customer?.companyName || 'Unknown'}.${ext}`;
            const blob = new Blob([content], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            alert("Error generating artifact: " + err);
        }
    };

    const handleGenerateTestReport = async () => {
        if (!user) return;
        setGenerating(true);
        try {
            const aiData = await generateEdgeCaseReportContent();
            const customers = await fetchCustomerDirectory();
            const randomCustomer: Customer = customers[0] || {
                id: 'mock',
                companyName: 'Mock Corp',
                contactPerson: 'Mock Contact',
                position: 'Manager',
                address: '123 Mock Blvd',
                phone: '555-0123'
            };

            const newId = generateUUID();
            const now = new Date().toISOString();

            const newReport: ServiceReport = {
                id: newId,
                status: 'DRAFT',
                template: 'SERVICE',
                createdAt: now,
                updatedAt: now,
                createdBy: user.email,
                technicianName: user.name,
                arrivalDate: now.split('T')[0],
                departureDate: '',
                serviceTypes: ['Billable Repair', 'Emergency'],
                customer: randomCustomer,
                machine: aiData.machine || { serialNumber: 'UNK', modelNumber: 'UNK', machineType: 'UNK', controllerType: 'UNK', softwareVersion: '' },
                summary: aiData.summary || "Auto-generated stress test report.",
                attachments: [],
                parts: [],
                followUpRequired: true,
                designSuggestion: { current: '', problem: '', change: '' },
                internalSuggestion: 'Check AI generated content for accuracy.',
                toolsBought: [],
                toolsUsed: [],
                newNameplates: [],
                issues: (aiData.issues || []).map((iss: any) => ({
                    id: generateUUID(),
                    title: iss.title || 'Untitled Issue',
                    category: iss.category || 'Not Specified',
                    resolved: !!iss.resolved,
                    urgency: iss.urgency || 'Medium',
                    description: iss.description || '',
                    proposedFixes: (iss.proposedFixes || []).map((t: string) => ({ id: generateUUID(), text: t })),
                    troubleshootingSteps: (iss.troubleshootingSteps || []).map((t: string) => ({ id: generateUUID(), text: t })),
                    rootCause: iss.rootCause || '',
                    fixApplied: iss.fixApplied || '',
                    verifiedBy: iss.verifiedBy || '',
                    notes: iss.notes || '',
                    solutionSummary: iss.solutionSummary || '',
                    attachments: [],
                    parts: [],
                    addToMfgReport: Math.random() > 0.5,
                    followUpRequired: Math.random() > 0.8
                })),
                _syncState: { lastSaved: now, dirty: true, uploadQueue: [], version: 1, isOffline: false }
            };

            await saveLocalReport(newReport);
            setCurrentReportId(newId);
            setView('editor');

        } catch (e) {
            console.error(e);
            alert("Failed to generate report: " + e);
        } finally {
            setGenerating(false);
        }
    };

    // Show loading while checking auth
    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center bg-slate-950">
                <div className="text-center">
                    <i className="fa-solid fa-circle-notch fa-spin text-red-500 text-3xl mb-4"></i>
                    <p className="text-slate-400">Loading...</p>
                </div>
            </div>
        );
    }

    // Show login page if not authenticated
    if (!user) {
        return <LoginPage />;
    }

    // Editor view
    if (view === 'editor') {
        return (
            <ReportEditor
                reportId={currentReportId}
                initialTemplate={initialTemplate}
                userEmail={user.email}
                userName={user.name}
                onClose={() => {
                    setView('list');
                    setCurrentReportId(undefined);
                    loadReports();
                }}
            />
        );
    }

    // Dashboard view
    return (
        <>
        {/* <AuthDebugPanel /> */}
        <Layout user={user} onLogout={signOut} title="Dashboard" actions={
            <button onClick={() => setShowNewReportModal(true)} className="bg-green-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold shadow hover:bg-green-500 transition-colors flex items-center gap-1.5">
                <i className="fa-solid fa-plus"></i>
                <span>New Report</span>
            </button>
        }>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Dashboard Area */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-5">
                        <div className="flex justify-between items-center mb-3">
                             <h2 className="text-base font-semibold text-white">Recent Reports</h2>
                             <button onClick={loadReports} className="text-slate-500 hover:text-red-500 transition-colors text-sm"><i className="fa-solid fa-rotate-right"></i></button>
                        </div>

                        {loadingReports ? (
                            <div className="py-6 text-center text-slate-500 text-xs"><i className="fa-solid fa-circle-notch fa-spin"></i> Loading...</div>
                        ) : reports.length === 0 ? (
                            <div className="border border-dashed border-slate-700 rounded-lg p-6 text-center">
                                <p className="text-slate-500 italic text-xs">No recent reports found locally.</p>
                                <button onClick={() => setShowNewReportModal(true)} className="mt-2 text-red-500 hover:underline text-xs">Create your first report</button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {reports.map(report => (
                                    <div
                                        key={report.id}
                                        onClick={() => { setCurrentReportId(report.id); setInitialTemplate(report.template || 'SERVICE'); setView('editor'); }}
                                        className="border border-slate-700 rounded-lg p-3 hover:border-red-500/50 hover:bg-slate-800/50 transition-all cursor-pointer bg-slate-800/30 group relative"
                                    >
                                        <div className="flex justify-between items-start mb-1.5">
                                            <div>
                                                <h3 className="font-bold text-white text-sm">
                                                    {report.customer?.companyName || 'Unassigned Customer'}
                                                </h3>
                                                <div className="text-[10px] text-slate-500 flex items-center gap-2">
                                                    <span><i className="fa-regular fa-calendar mr-1"></i> {report.arrivalDate}</span>
                                                    <span>•</span>
                                                    <span className="font-mono">{report.reportId || 'Draft'}</span>
                                                    {report.template && (
                                                        <span className="bg-slate-800 px-1.5 rounded text-slate-400 border border-slate-600">{report.template}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${report.status === 'COMPLETED' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-slate-700 text-slate-400 border border-slate-600'}`}>
                                                {report.status}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-400 line-clamp-2 mb-1.5">
                                            {report.summary || 'No summary provided...'}
                                        </div>

                                        {/* Actions Footer */}
                                        <div className="flex justify-between items-center text-[10px] text-slate-500 border-t border-slate-700 pt-1.5 mt-1.5">
                                            <span>Issues: {report.issues?.length || 0}</span>

                                            {report.status === 'COMPLETED' && (
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={(e) => downloadArtifact(e, report, 'html')}
                                                        className="text-red-400 hover:text-red-300 bg-red-500/10 px-2 py-0.5 rounded border border-red-500/20"
                                                        title="Download HTML Report"
                                                    >
                                                        HTML
                                                    </button>
                                                    <button
                                                        onClick={(e) => downloadArtifact(e, report, 'json')}
                                                        className="text-slate-400 hover:text-slate-300 bg-slate-700 px-2 py-0.5 rounded border border-slate-600"
                                                        title="Download JSON Data"
                                                    >
                                                        JSON
                                                    </button>
                                                </div>
                                            )}

                                            {report.status !== 'COMPLETED' && (
                                                <span className="group-hover:text-red-500 transition-colors">Open <i className="fa-solid fa-chevron-right ml-1"></i></span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">System Status</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <StatusCard icon="fa-wifi" label="WorkDrive Connection" status="Connected" color="green" />
                        <StatusCard icon="fa-database" label="Local Cache" status="Active" color="green" />
                        <StatusCard icon="fa-bolt" label="Gemini AI" status="Ready" color="red" />
                    </div>
                </div>

                {/* Admin / Sidebar Area */}
                <div className="space-y-4">
                    <RequireAdmin silent>
                        <div className="bg-slate-900 border border-slate-800 text-white rounded-xl shadow-lg p-4">
                            <h3 className="font-bold text-xs uppercase tracking-wider mb-3 border-b border-slate-700 pb-2 flex items-center text-red-500">
                                <i className="fa-solid fa-user-shield mr-2"></i> Admin Tools
                            </h3>
                            <p className="text-[10px] text-slate-400 mb-3">
                                Use these tools to manage system configuration and stress-test the application.
                            </p>

                            <div className="space-y-2">
                                <button
                                    onClick={handleGenerateTestReport}
                                    disabled={generating}
                                    className="w-full bg-red-600 hover:bg-red-500 disabled:bg-red-900 disabled:text-red-300 text-white text-xs font-medium py-2 px-2.5 rounded-lg flex items-center justify-between transition-colors"
                                >
                                    <span>
                                        {generating ? <i className="fa-solid fa-circle-notch fa-spin mr-1.5"></i> : <i className="fa-solid fa-robot mr-1.5"></i>}
                                        {generating ? 'Generating...' : 'Gen. Edge Case Report'}
                                    </span>
                                    <i className="fa-solid fa-chevron-right text-[10px] opacity-50"></i>
                                </button>
                            </div>
                        </div>
                    </RequireAdmin>

                    {/* User Management Panel */}
                    <AdminUsersPanel />
                </div>
            </div>
        </Layout>

        {showNewReportModal && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl max-w-md w-full p-6 relative">
                    <button onClick={() => setShowNewReportModal(false)} className="absolute top-4 right-4 text-slate-500 hover:text-white">
                        <i className="fa-solid fa-xmark text-lg"></i>
                    </button>
                    
                    <h2 className="text-xl font-bold text-white mb-2">Create New Report</h2>
                    <p className="text-slate-400 text-sm mb-6">Select the type of report you want to start.</p>
                    
                    <div className="space-y-3">
                        <button onClick={() => handleNewReport('SERVICE')} className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 p-4 rounded-lg flex items-center gap-4 transition-colors group">
                            <div className="w-10 h-10 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center group-hover:bg-red-500 group-hover:text-white transition-colors">
                                <i className="fa-solid fa-screwdriver-wrench"></i>
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-white">Service Report</div>
                                <div className="text-xs text-slate-400">Issues, troubleshooting, and summary</div>
                            </div>
                        </button>

                        <button onClick={() => handleNewReport('INSTALLATION')} className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 p-4 rounded-lg flex items-center gap-4 transition-colors group">
                            <div className="w-10 h-10 rounded-full bg-blue-500/20 text-blue-500 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors">
                                <i className="fa-solid fa-truck-ramp-box"></i>
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-white">Installation</div>
                                <div className="text-xs text-slate-400">Machine setup and initial tests</div>
                            </div>
                        </button>

                        <button onClick={() => handleNewReport('COMMISSIONING')} className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 p-4 rounded-lg flex items-center gap-4 transition-colors group">
                            <div className="w-10 h-10 rounded-full bg-purple-500/20 text-purple-500 flex items-center justify-center group-hover:bg-purple-500 group-hover:text-white transition-colors">
                                <i className="fa-solid fa-clipboard-check"></i>
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-white">Commissioning</div>
                                <div className="text-xs text-slate-400">Final checklist and sign-off</div>
                            </div>
                        </button>

                        <button onClick={() => handleNewReport('TRAINING')} className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 p-4 rounded-lg flex items-center gap-4 transition-colors group">
                            <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-colors">
                                <i className="fa-solid fa-chalkboard-user"></i>
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-white">Training</div>
                                <div className="text-xs text-slate-400">Trainee records and topics</div>
                            </div>
                        </button>

                        <button onClick={() => handleNewReport('CUTTING')} className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 p-4 rounded-lg flex items-center gap-4 transition-colors group">
                            <div className="w-10 h-10 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center group-hover:bg-green-500 group-hover:text-white transition-colors">
                                <i className="fa-solid fa-scissors"></i>
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-white">Cutting Conditions</div>
                                <div className="text-xs text-slate-400">Speed, feed, and material data</div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

const StatusCard: React.FC<{icon: string, label: string, status: string, color: string}> = ({icon, label, status, color}) => (
    <div className="bg-slate-900 border border-slate-800 p-3 rounded-xl shadow-lg flex items-center space-x-2.5">
        <div className={`w-8 h-8 rounded-lg bg-${color}-500/20 flex items-center justify-center text-${color}-400 border border-${color}-500/30 text-sm`}>
            <i className={`fa-solid ${icon}`}></i>
        </div>
        <div>
            <div className="text-[10px] text-slate-500 uppercase">{label}</div>
            <div className="font-semibold text-white text-sm">{status}</div>
        </div>
    </div>
);

export default ServiceReportApp;
